import { useMutation, useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import {
  CheckCircle2,
  CircleSlash,
  ExternalLink,
  ListPlus,
  RefreshCw,
  ShieldCheck,
  TriangleAlert,
  Unplug,
  Wallet,
} from 'lucide-react'
import { useId, useRef, useState, type ReactNode } from 'react'
import { toast } from 'sonner'

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Spinner } from '@/components/ui/spinner'
import { Textarea } from '@/components/ui/textarea'
import { Page, PageHeader } from '@/components/shell'
import { ContractWalletBar, sameAddress, useContractWallet } from '@/features/contracts/wallet-bar'
import {
  api,
  ApiError,
  type ContractBatchOperationResult,
  type ContractListStatus,
  type ContractOperationPlan,
  type ContractOperationResult,
} from '@/lib/api'
import {
  contractWalletErrorMessage,
  readContractListState,
  sendContractListBatchOperation,
  sendContractListOperation,
  waitForContractOperation,
  X_LAYER,
  type ContractWriteKind,
} from '@/lib/contract-wallet'
import { explorerAddressUrl, explorerTxUrl, shortAddress } from '@/lib/format'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/dashboard/contracts/lists')({
  component: ContractListsPage,
})

const ADDRESS_RE = /^0x[0-9a-fA-F]{40}$/

export function isEvmAddress(value: string): boolean {
  return ADDRESS_RE.test(value.trim())
}

function ContractListsPage() {
  const status = useQuery({
    queryKey: ['contract-lists-status'],
    queryFn: () => api.get<ContractListStatus>('/contracts/lists/status'),
    staleTime: 30_000,
    retry: false,
  })
  const { wallet, walletBusy, connect, switchChain, disconnect } = useContractWallet()

  const chainReady = status.data?.ready === true && wallet.chainId === X_LAYER.id
  const ownerReady = chainReady && sameAddress(wallet.address, status.data?.owner)
  const manager = status.data?.manager ?? null

  return (
    <Page>
      <PageHeader
        title="名单管理"
        description="连接 Ignix Manager owner 钱包，快速追加第三方检测池或合约免税端点。私钥始终留在钱包内，每次操作都会由服务端复核链上交易并写入审计流水。"
      />

      <ContractWalletBar
        status={status.data}
        isLoading={status.isLoading}
        isError={status.isError}
        error={status.error}
        onRetry={() => void status.refetch()}
        wallet={wallet}
        walletBusy={walletBusy}
        expected={[{ label: 'Manager owner', address: status.data?.owner ?? null }]}
        onConnect={connect}
        onDisconnect={disconnect}
        onSwitchChain={switchChain}
      />

      <div className="grid gap-3.5 lg:grid-cols-2">
        <OperationCard
          kind="detectedPool"
          revocable={false}
          ready={ownerReady}
          account={wallet.address}
          manager={manager}
          icon={<ListPlus className="size-4 text-cyan" />}
          title="添加检测池"
          description="将已确认的第三方池登记到指定代币，保护期内后续交易会纳入收税或阻断判定。"
          targetLabel="池合约地址"
          targetPlaceholder="0x… pool"
          stateLabels={{ on: '已登记为检测池', off: '尚未登记' }}
          copy={{
            add: {
              submitLabel: '添加检测池',
              confirmTitle: '确认追加检测池？',
              warning:
                '检测池只能在代币保护期内追加，并且合约不提供移除方法。请先在区块浏览器核对地址。',
            },
          }}
        />

        <OperationCard
          kind="taxExemption"
          revocable
          ready={ownerReady}
          account={wallet.address}
          manager={manager}
          icon={<ShieldCheck className="size-4 text-cyan" />}
          title="免税白名单"
          description="为收税代币增删经过审核的合约集成端点。添加与撤销共用同一个 setTaxExempt 入口，两个方向都会核验链上交易并写入审计。"
          targetLabel="免税合约地址"
          targetPlaceholder="0x… contract"
          stateLabels={{ on: '当前免税', off: '当前不免税' }}
          copy={{
            add: {
              submitLabel: '加入免税白名单',
              confirmTitle: '确认添加免税端点？',
              warning:
                '公共 Adapter 一旦免税，所有经过它且与池子相连的买卖都可能免税。只能添加代码行为已审核的合约端点。',
            },
            revoke: {
              submitLabel: '撤销免税',
              confirmTitle: '确认撤销免税端点？',
              warning:
                '撤销后该地址经过池子的买卖会立即重新计税。如果它是仍在使用中的集成端点，正在进行的交易可能因滑点不足而失败。',
            },
          }}
        />
      </div>
    </Page>
  )
}

type OperationCopy = {
  submitLabel: string
  confirmTitle: string
  warning: string
}

type OperationCardProps = {
  kind: ContractWriteKind
  /**
   * 合约层是否提供撤销入口。
   *
   * detectedPool 为 false：IgnixToken 只有 addDetectedPools，服务端也会把
   * `enabled: false` 拒成 400。所以这张卡片**不渲染撤销开关**——摆一个点下去
   * 必然失败的按钮，比不摆更糟。
   */
  revocable: boolean
  ready: boolean
  account: string | null
  manager: string | null
  icon: ReactNode
  title: string
  description: string
  targetLabel: string
  targetPlaceholder: string
  /** 单币模式下读到的链上现状怎么说。 */
  stateLabels: { on: string; off: string }
  copy: { add: OperationCopy; revoke?: OperationCopy }
}

type SingleAuditPayload = {
  kind: ContractWriteKind
  enabled: boolean
  tokenAddress: string
  targetAddress: string
  reason: string
  txHash: string
}

type BatchAuditPayload = {
  kind: ContractWriteKind
  enabled: boolean
  tokenAddresses: string[]
  targetAddress: string
  reason: string
  txHash: string
}

type OperationRequest = { mode: 'single' } | { mode: 'batch'; tokenAddresses: string[] }

type AuditPending =
  | {
      endpoint: '/contracts/lists/operations'
      payload: SingleAuditPayload
      request: Extract<OperationRequest, { mode: 'single' }>
    }
  | {
      endpoint: '/contracts/lists/batch-operations'
      payload: BatchAuditPayload
      request: Extract<OperationRequest, { mode: 'batch' }>
    }

class SubmittedTransactionError extends Error {
  constructor(
    readonly pending: AuditPending,
    cause: unknown,
  ) {
    super(contractWalletErrorMessage(cause))
  }
}

class ScopeChangedError extends Error {
  constructor(readonly plan: ContractOperationPlan) {
    super('代币范围已变化，请核对最新范围后重新确认。')
  }
}

class AuditPendingError extends Error {
  readonly terminal: boolean

  constructor(
    readonly pending: AuditPending,
    cause: unknown,
  ) {
    super(cause instanceof ApiError ? cause.message : '审计接口暂时不可用。')
    this.terminal = cause instanceof ApiError && cause.errorCode === 'transaction_failed'
  }
}

const AUDIT_PENDING_STORAGE_PREFIX = 'contract-list-audit-pending:'

function auditPendingStorageKey(kind: ContractWriteKind) {
  return `${AUDIT_PENDING_STORAGE_PREFIX}${kind}`
}

function readStoredAuditPending(kind: ContractWriteKind): AuditPending | null {
  if (typeof window === 'undefined') return null

  try {
    const raw = window.sessionStorage.getItem(auditPendingStorageKey(kind))
    if (!raw) return null
    const value = JSON.parse(raw) as AuditPending
    if (
      (value.endpoint !== '/contracts/lists/operations' &&
        value.endpoint !== '/contracts/lists/batch-operations') ||
      value.payload.kind !== kind ||
      // enabled 决定补写的是「添加」还是「撤销」，恢复时错一个方向就等于
      // 给一笔真实交易补了一条内容相反的审计。宁可丢弃也不猜。
      typeof value.payload.enabled !== 'boolean' ||
      (kind === 'detectedPool' && value.payload.enabled !== true) ||
      !/^0x[0-9a-fA-F]{64}$/.test(value.payload.txHash) ||
      (value.endpoint === '/contracts/lists/operations' && value.request.mode !== 'single') ||
      (value.endpoint === '/contracts/lists/batch-operations' && value.request.mode !== 'batch')
    ) {
      throw new Error('invalid pending operation')
    }
    return value
  } catch {
    try {
      window.sessionStorage.removeItem(auditPendingStorageKey(kind))
    } catch {
      // Storage may be disabled. The in-memory lock still prevents duplicate sends in this tab.
    }
    return null
  }
}

function storeAuditPending(kind: ContractWriteKind, pending: AuditPending | null) {
  try {
    if (pending) {
      window.sessionStorage.setItem(auditPendingStorageKey(kind), JSON.stringify(pending))
    } else {
      window.sessionStorage.removeItem(auditPendingStorageKey(kind))
    }
  } catch {
    // Do not let a storage policy failure unlock a transaction that already has a hash.
  }
}

function sameTokenAddresses(left: string[], right: string[]): boolean {
  return (
    left.length === right.length &&
    left.every((token, index) => token.toLowerCase() === right[index]?.toLowerCase())
  )
}

type TokenScope = 'single' | 'all'

const SCOPE_OPTIONS = [
  { value: 'single', label: '单个代币' },
  { value: 'all', label: '全部代币' },
] as const satisfies readonly { value: TokenScope; label: string }[]

/** 免税那张卡片的方向开关。检测池不可撤销，所以它根本不渲染这个控件。 */
const DIRECTION_OPTIONS = [
  { value: 'add', label: '添加' },
  { value: 'revoke', label: '撤销', tone: 'danger' },
] as const satisfies readonly { value: 'add' | 'revoke'; label: string; tone?: 'danger' }[]

/**
 * 分段控件。
 *
 * 底轨 + 选中块，而不是四段裸文字加下划线：两组开关并排出现在同一行，
 * 没有各自的容器时它们会被读成一组四选一。
 *
 * 选中块的着色走 `--fill`（中性表面填充的着色源，两个主题取值相反），
 * 所以一份写法在浅底上压暗、在深底上提亮，都读作"选中"。唯一的例外是
 * 「撤销」——那是破坏性方向，选中时染成 rose，让当前模式在余光里就能看出来。
 */
function SegmentedSwitch<T extends string>({
  label,
  options,
  value,
  disabled,
  disabledHint,
  onChange,
}: {
  label: string
  options: readonly { value: T; label: string; tone?: 'danger' }[]
  value: T
  disabled: boolean
  disabledHint?: string
  onChange: (next: T) => void
}) {
  const labelId = useId()
  const optionRefs = useRef<Array<HTMLButtonElement | null>>([])

  const move = (delta: number) => {
    const index = options.findIndex((option) => option.value === value)
    const nextIndex = (index + delta + options.length) % options.length
    const next = options[nextIndex]
    if (next) {
      onChange(next.value)
      optionRefs.current[nextIndex]?.focus()
    }
  }

  return (
    <div className="flex min-w-0 items-center gap-2.5">
      <span id={labelId} className="shrink-0 text-[11px] text-mut2">
        {label}
      </span>
      <div
        role="radiogroup"
        aria-labelledby={labelId}
        title={disabled ? disabledHint : undefined}
        className="flex shrink-0 items-center gap-0.5 rounded-apple-sm border border-edge/[0.09] bg-edge/[0.045] p-[3px]"
        onKeyDown={(event) => {
          if (disabled) return
          if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
            event.preventDefault()
            move(1)
          } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
            event.preventDefault()
            move(-1)
          }
        }}
      >
        {options.map((option, index) => {
          const active = option.value === value
          const danger = option.tone === 'danger'
          return (
            <button
              key={option.value}
              ref={(node) => {
                optionRefs.current[index] = node
              }}
              type="button"
              role="radio"
              aria-checked={active}
              tabIndex={active ? 0 : -1}
              disabled={disabled}
              className={cn(
                'pressable h-7 rounded-[7px] px-3 text-[12px] transition-colors outline-none',
                'focus-visible:ring-2 focus-visible:ring-cyan/40',
                active && 'font-medium',
                active &&
                  !disabled &&
                  (danger ? 'bg-rose/12 text-rose' : 'bg-fill/[0.09] text-txt'),
                active && disabled && 'bg-fill/[0.06] text-mut',
                !active && !disabled && 'text-mut hover:text-txt',
                !active && disabled && 'text-dim',
                disabled && 'cursor-not-allowed',
              )}
              onClick={() => onChange(option.value)}
            >
              {option.label}
            </button>
          )
        })}
      </div>
    </div>
  )
}

/**
 * 提交按钮为什么点不下去。
 *
 * 禁用态只是变灰，不说理由——「链上已经是目标状态」和「批量还没核验范围」
 * 这两条从界面上完全看不出来，只能一个字段一个字段地试。
 */
function submitBlocker(state: {
  ready: boolean
  allMode: boolean
  tokenValid: boolean
  targetValid: boolean
  targetLabel: string
  reasonValid: boolean
  alreadyDesired: boolean
  /** 批量模式下已核验出的代币数；null 表示还没核验。 */
  planCount: number | null
}): string | null {
  if (!state.ready) return '需要先用 Manager owner 钱包连接 X Layer。'
  if (!state.allMode && !state.tokenValid) return '请填写有效的代币合约地址。'
  if (!state.targetValid) return `请填写有效的${state.targetLabel}。`
  if (state.allMode && state.planCount === null) return '请先核验实际操作范围。'
  if (state.allMode && state.planCount === 0) return '当前范围内没有需要提交的代币。'
  if (!state.reasonValid) return '操作理由至少需要 10 个字符。'
  if (state.alreadyDesired) return '链上已经是目标状态，这一步不会改变任何东西。'
  return null
}

function OperationCard(props: OperationCardProps) {
  const [tokenAddress, setTokenAddress] = useState('')
  const [targetAddress, setTargetAddress] = useState('')
  const [reason, setReason] = useState('')
  const [scope, setScope] = useState<TokenScope>('single')
  const [bulkPlan, setBulkPlan] = useState<ContractOperationPlan | null>(null)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [lastResult, setLastResult] = useState<
    ContractOperationResult | ContractBatchOperationResult | null
  >(null)
  const [auditPending, setAuditPending] = useState<AuditPending | null>(() =>
    readStoredAuditPending(props.kind),
  )
  /**
   * 刷新后恢复的待审计交易决定了方向：补写「撤销」时界面必须也停在撤销那一档，
   * 否则运营看到的是「添加」的文案，点下去补写的却是撤销。
   */
  const [enabled, setEnabled] = useState<boolean>(
    () => readStoredAuditPending(props.kind)?.payload.enabled ?? true,
  )

  const allMode = scope === 'all'
  const tokenValid = isEvmAddress(tokenAddress)
  const targetValid = isEvmAddress(targetAddress)
  const trimmedReason = reason.trim()
  const reasonValid = trimmedReason.length >= 10 && trimmedReason.length <= 200
  const copy = (enabled ? props.copy.add : props.copy.revoke) ?? props.copy.add

  /**
   * 单币模式下的链上现状。只读 `pools()` / `taxExempt()`，不需要钱包，
   * 所以连钱包之前就能先看清楚「现在到底是什么状态」。
   */
  const currentState = useQuery({
    queryKey: [
      'contract-list-state',
      props.kind,
      tokenAddress.trim().toLowerCase(),
      targetAddress.trim().toLowerCase(),
    ],
    queryFn: () =>
      readContractListState({
        kind: props.kind,
        tokenAddress: tokenAddress.trim(),
        targetAddress: targetAddress.trim(),
      }),
    enabled: !allMode && tokenValid && targetValid,
    retry: false,
    staleTime: 15_000,
  })

  // 读不到就不拦——链上模拟会兜底。只有明确读到「已经是目标状态」才挡。
  const alreadyDesired = !allMode && currentState.data === enabled

  const planMutation = useMutation({
    mutationFn: () =>
      api.post<ContractOperationPlan>('/contracts/lists/operation-plan', {
        kind: props.kind,
        enabled,
        targetAddress: targetAddress.trim(),
      }),
    onSuccess: (plan) => {
      setBulkPlan(plan)
      if (plan.data.length === 0) {
        toast.info('没有需要处理的代币', {
          description: !enabled
            ? '当前范围内没有需要撤销免税的代币。'
            : props.kind === 'detectedPool'
              ? '当前范围内没有可登记该检测池的代币。'
              : '当前范围内没有可为该地址新增免税的收税代币。',
        })
      }
    },
    onError: (error) => {
      toast.error('核验代币范围失败', { description: contractWalletErrorMessage(error) })
    },
  })

  const mutation = useMutation({
    mutationFn: async (request: OperationRequest) => {
      if (auditPending) {
        try {
          if (auditPending.endpoint === '/contracts/lists/operations') {
            return await api.post<ContractOperationResult>(
              auditPending.endpoint,
              auditPending.payload,
            )
          }
          return await api.post<ContractBatchOperationResult>(
            auditPending.endpoint,
            auditPending.payload,
          )
        } catch (error) {
          throw new AuditPendingError(auditPending, error)
        }
      }

      if (!props.account || !props.manager) throw new Error('请先连接 Manager owner 钱包。')

      const target = targetAddress.trim()
      let tokenAddresses: string[]
      if (request.mode === 'batch') {
        const refreshedPlan = await api.post<ContractOperationPlan>(
          '/contracts/lists/operation-plan',
          { kind: props.kind, enabled, targetAddress: target },
        )
        const refreshedAddresses = refreshedPlan.data.map((token) => token.tokenAddress)
        if (!sameTokenAddresses(request.tokenAddresses, refreshedAddresses)) {
          throw new ScopeChangedError(refreshedPlan)
        }
        tokenAddresses = refreshedAddresses
      } else {
        tokenAddresses = [tokenAddress.trim()]
      }
      const txHash =
        request.mode === 'batch'
          ? await sendContractListBatchOperation({
              kind: props.kind,
              enabled,
              account: props.account,
              manager: props.manager,
              tokenAddresses,
              targetAddress: target,
            })
          : await sendContractListOperation({
              kind: props.kind,
              enabled,
              account: props.account,
              manager: props.manager,
              tokenAddress: tokenAddresses[0]!,
              targetAddress: target,
            })

      const pending: AuditPending =
        request.mode === 'batch'
          ? {
              endpoint: '/contracts/lists/batch-operations',
              payload: {
                kind: props.kind,
                enabled,
                tokenAddresses,
                targetAddress: target,
                reason: trimmedReason,
                txHash,
              },
              request,
            }
          : {
              endpoint: '/contracts/lists/operations',
              payload: {
                kind: props.kind,
                enabled,
                tokenAddress: tokenAddresses[0]!,
                targetAddress: target,
                reason: trimmedReason,
                txHash,
              },
              request,
            }
      setAuditPending(pending)
      storeAuditPending(props.kind, pending)

      let receipt: Awaited<ReturnType<typeof waitForContractOperation>>
      try {
        receipt = await waitForContractOperation(txHash)
      } catch (error) {
        throw new SubmittedTransactionError(pending, error)
      }
      if (receipt.status !== 'success') {
        setAuditPending(null)
        storeAuditPending(props.kind, null)
        throw new Error('链上交易已回滚。')
      }
      try {
        if (pending.endpoint === '/contracts/lists/operations') {
          return await api.post<ContractOperationResult>(pending.endpoint, pending.payload)
        }
        return await api.post<ContractBatchOperationResult>(pending.endpoint, pending.payload)
      } catch (error) {
        throw new AuditPendingError(pending, error)
      }
    },
    onSuccess: (result, request) => {
      setLastResult(result)
      setAuditPending(null)
      storeAuditPending(props.kind, null)
      setConfirmOpen(false)

      if (request.mode === 'batch') {
        setBulkPlan(null)
        setTargetAddress('')
        setReason('')
        toast.success(enabled ? '全部代币已处理完成' : '全部代币已撤销', {
          description: `${request.tokenAddresses.length} 个代币已在同一笔交易中确认并写入审计。`,
        })
        return
      }

      setTargetAddress('')
      setReason('')
      void currentState.refetch()
      toast.success(
        props.kind === 'detectedPool' ? '检测池已添加' : enabled ? '免税已开启' : '免税已撤销',
        { description: `交易 ${shortAddress(result.txHash)} 已确认并写入审计` },
      )
    },
    onError: (error, request) => {
      if (error instanceof SubmittedTransactionError) {
        setAuditPending(error.pending)
        setConfirmOpen(false)
        toast.warning('交易已提交，等待链上确认', {
          description: '请勿重复提交。可用原交易重新核验链上状态并补写审计。',
        })
        return
      }

      if (error instanceof AuditPendingError) {
        if (error.terminal) {
          setAuditPending(null)
          storeAuditPending(props.kind, null)
          setConfirmOpen(false)
          toast.error('链上交易已回滚', {
            description: '原交易未生效，已解除锁定；核对参数后可以重新提交。',
          })
          return
        }
        setAuditPending(error.pending)
        setConfirmOpen(false)
        toast.warning('交易尚未完成审计', {
          description: `${error.message} 可使用本卡片的补写入口重试审计，不会再次发送交易。`,
        })
        return
      }

      if (error instanceof ScopeChangedError) {
        setBulkPlan(error.plan)
        setConfirmOpen(false)
        toast.warning('代币范围已变化', {
          description: '已刷新为最新范围，请核对数量后重新确认。',
        })
        return
      }

      toast.error(request.mode === 'batch' ? '批量交易失败' : '钱包操作失败', {
        description: contractWalletErrorMessage(error),
      })
    },
  })

  const formReady =
    props.ready &&
    targetValid &&
    reasonValid &&
    !alreadyDesired &&
    (allMode ? Boolean(bulkPlan?.data.length) : tokenValid) &&
    !mutation.isPending &&
    !auditPending
  const locked = Boolean(auditPending || mutation.isPending || planMutation.isPending)

  const verifyPlan = () => {
    setBulkPlan(null)
    planMutation.mutate()
  }

  const updateTargetAddress = (value: string) => {
    setTargetAddress(value)
    if (allMode) {
      setBulkPlan(null)
    }
  }

  const blocker = auditPending
    ? '交易已经上链，补写审计成功后才能继续下一次操作。'
    : submitBlocker({
        ready: props.ready,
        allMode,
        tokenValid,
        targetValid,
        targetLabel: props.targetLabel,
        reasonValid,
        alreadyDesired,
        planCount: bulkPlan ? bulkPlan.data.length : null,
      })

  return (
    <section className="surface @container flex flex-col p-6 lg:min-h-[510px]">
      <div className="flex items-start gap-3">
        <div className="surface-inset flex size-9 shrink-0 items-center justify-center">
          {props.icon}
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="text-[15px] font-medium tracking-tight text-txt">{props.title}</h2>
          {/*
            限宽到一个可读的行长：卡片能有 800px 宽，一行铺满的说明没人会读完。
            留两行的位：两张卡片在同一行网格里并排，说明一长一短会让下面每一行
            都错开十几像素——同构的孪生卡片错位比留白更显眼。
          */}
          <p className="mt-1 min-h-[2lh] max-w-[62ch] text-[12px] leading-relaxed text-mut2">
            {props.description}
          </p>
        </div>
      </div>

      {/*
        方向与范围是两个互相独立的维度，各自带一条底轨和一个可见的组名。
        原来是四段裸文字并排、只靠间距分组——「添加 撤销 单个代币 全部代币」
        读起来像一组四选一，而选错方向意味着发出一笔内容相反的链上交易。
      */}
      <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-3 border-t border-line/50 pt-4">
        {/* 占的正是另一张卡片放「操作方向」的位置——这里没有方向可选，原因就写在这。 */}
        {!props.revocable && (
          <p className="inline-flex items-center gap-1.5 rounded-apple-sm bg-warn/[0.08] px-2.5 py-1.5 text-[11px] leading-none text-warn">
            <CircleSlash className="size-3 shrink-0" aria-hidden="true" />
            不可撤销：合约没有移除入口，这里也不提供撤销操作
          </p>
        )}
        {props.revocable && (
          <SegmentedSwitch
            label="操作方向"
            options={DIRECTION_OPTIONS}
            value={enabled ? 'add' : 'revoke'}
            disabled={locked}
            disabledHint="当前操作尚未结束，完成后可切换方向"
            onChange={(next) => {
              setEnabled(next === 'add')
              setBulkPlan(null)
            }}
          />
        )}
        <SegmentedSwitch
          label="代币范围"
          options={SCOPE_OPTIONS}
          value={scope}
          disabled={locked}
          disabledHint="当前操作尚未结束，完成后可切换范围"
          onChange={(next) => {
            setScope(next)
            setBulkPlan(null)
          }}
        />
      </div>

      <form
        className="mt-5 flex flex-1 flex-col"
        onSubmit={(event) => {
          event.preventDefault()
          if (formReady) setConfirmOpen(true)
        }}
      >
        <div key={`${scope}:${enabled}`} className="rise space-y-4">
          {/*
            卡片够宽时两个地址并排。容器查询而不是断点：这张卡片在 lg 网格里
            占半列，宽度取决于视口减侧栏，viewport 断点量的不是它。
          */}
          <div className="grid gap-4 @3xl:grid-cols-2">
            {!allMode && (
              <AddressField
                id={`${props.kind}-token`}
                label="代币合约地址"
                placeholder="0x… token"
                value={tokenAddress}
                disabled={locked}
                onChange={setTokenAddress}
              />
            )}
            <AddressField
              id={`${props.kind}-target`}
              label={props.targetLabel}
              placeholder={props.targetPlaceholder}
              value={targetAddress}
              disabled={locked}
              onChange={updateTargetAddress}
            />
          </div>

          {!allMode && (
            <CurrentStatePanel
              query={currentState}
              labels={props.stateLabels}
              desired={enabled}
              revocable={props.revocable}
              ready={tokenValid && targetValid}
            />
          )}

          {allMode && (
            <p className="text-[11px] leading-relaxed text-mut2" aria-live="polite">
              {props.kind === 'detectedPool'
                ? '同一个池地址会在一笔原子交易中登记到全部核验通过的代币，只需确认一次钱包；Gas 会随代币数量增长。'
                : enabled
                  ? '同一个免税端点会在一笔原子交易中写入全部核验通过的收税代币，只需确认一次钱包；Gas 会随代币数量增长。'
                  : '同一个免税端点会在一笔原子交易中从全部仍免税的代币上撤销，只需确认一次钱包；Gas 会随代币数量增长。'}
            </p>
          )}

          {allMode && (
            <BulkPlanPanel
              kind={props.kind}
              enabled={enabled}
              plan={bulkPlan}
              planning={planMutation.isPending}
              canVerify={props.ready && targetValid && !locked}
              onVerify={verifyPlan}
            />
          )}

          <div className="space-y-1.5">
            <div className="flex items-center justify-between gap-3">
              <Label htmlFor={`${props.kind}-reason`} className="text-[12.5px]">
                操作理由
              </Label>
              <span
                className={cn(
                  'tnum text-[11px]',
                  reason.length > 0 && !reasonValid ? 'text-rose' : 'text-mut2',
                )}
              >
                {reason.length}/200
              </span>
            </div>
            <Textarea
              id={`${props.kind}-reason`}
              value={reason}
              maxLength={200}
              disabled={locked}
              placeholder="说明核验依据、业务用途或工单编号（至少 10 个字符）"
              className="min-h-20 resize-none rounded-apple text-[12px] leading-relaxed"
              aria-invalid={reason.length > 0 && !reasonValid}
              onChange={(event) => setReason(event.target.value)}
            />
            {reason.length > 0 && !reasonValid && (
              <p className="text-[11px] text-rose">操作理由至少需要 10 个字符。</p>
            )}
          </div>
        </div>

        <div className="mt-5 flex items-start gap-2.5 rounded-apple bg-warn/[0.07] px-3.5 py-3 text-[11.5px] leading-relaxed text-mut">
          <TriangleAlert className="mt-px size-3.5 shrink-0 text-warn" />
          <span>{copy.warning}</span>
        </div>

        {lastResult && <TransactionNotice tone="success" hash={lastResult.txHash} />}
        {auditPending && <TransactionNotice tone="audit" hash={auditPending.payload.txHash} />}

        {/*
          提交行：按钮收回自然宽度，空出来的位置放一句「现在为什么点不下去」。
          横跨整张卡片的灰色大按钮既不像按钮，也不解释自己为什么是灰的——
          「已是目标状态」和「还没核验范围」这两条尤其看不出来。
        */}
        <div className="mt-auto flex flex-col gap-3 border-t border-line/50 pt-4 @md:flex-row @md:items-center @md:justify-between">
          <p className="min-w-0 text-[11.5px] leading-relaxed text-mut2">
            {blocker ?? '确认后由浏览器钱包签名，私钥不会离开钱包。'}
          </p>
          {auditPending ? (
            <Button
              type="button"
              className="pressable h-9 shrink-0 rounded-apple px-5 text-[13px]"
              disabled={mutation.isPending}
              onClick={() => mutation.mutate(auditPending.request)}
            >
              {mutation.isPending ? '核验并补写中…' : '补写审计记录'}
            </Button>
          ) : (
            <Button
              type="submit"
              className="pressable h-9 shrink-0 rounded-apple px-5 text-[13px]"
              disabled={!formReady}
            >
              {mutation.isPending
                ? '等待钱包或链上确认…'
                : allMode
                  ? `批量处理 ${bulkPlan?.data.length ?? 0} 个代币`
                  : copy.submitLabel}
            </Button>
          )}
        </div>
      </form>

      <AlertDialog
        open={confirmOpen}
        onOpenChange={(open) => {
          if (!mutation.isPending) setConfirmOpen(open)
        }}
      >
        <AlertDialogContent className="rounded-apple-xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[17px] text-txt">
              {allMode && bulkPlan
                ? props.kind === 'detectedPool'
                  ? `确认为 ${bulkPlan.data.length} 个代币登记检测池？`
                  : enabled
                    ? `确认为 ${bulkPlan.data.length} 个代币添加免税端点？`
                    : `确认从 ${bulkPlan.data.length} 个代币撤销免税端点？`
                : copy.confirmTitle}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[12.5px] leading-relaxed text-mut">
              {allMode && bulkPlan
                ? '钱包只会请求一次签名，并提交一笔原子批量交易。任意代币失败都会使整笔交易回滚。'
                : '请最后核对地址。确认后浏览器钱包会弹出交易签名，后台不会接触你的私钥。'}
            </AlertDialogDescription>
          </AlertDialogHeader>

          <dl className="surface-inset space-y-3 p-3.5 text-[11.5px]">
            <ConfirmRow label="签名钱包" value={props.account ?? ''} />
            <ConfirmRow
              label="代币范围"
              value={
                allMode && bulkPlan
                  ? `全部可操作代币 · ${bulkPlan.data.length} 个`
                  : tokenAddress.trim()
              }
              mono={!allMode}
            />
            <ConfirmRow label={props.targetLabel} value={targetAddress.trim()} />
            <ConfirmRow
              label="操作方向"
              value={
                props.revocable
                  ? enabled
                    ? '添加（setTaxExempt = true）'
                    : '撤销（setTaxExempt = false）'
                  : '添加（addDetectedPools，不可撤销）'
              }
              mono={false}
            />
            {allMode && bulkPlan && (
              <ConfirmRow
                label="核验排除"
                value={`${bulkPlan.totalIndexed - bulkPlan.data.length} 个（旧部署、已配置或模拟失败）`}
                mono={false}
              />
            )}
            <ConfirmRow label="操作理由" value={trimmedReason} mono={false} />
          </dl>

          <p
            className={cn(
              'flex items-start gap-2 text-[11.5px] leading-relaxed',
              props.kind === 'detectedPool'
                ? 'rounded-apple bg-rose/[0.06] px-3 py-2.5 text-rose'
                : 'text-warn',
            )}
          >
            <TriangleAlert className="mt-0.5 size-3.5 shrink-0" />
            {allMode && props.kind === 'detectedPool'
              ? `合约不提供移除检测池的方法。这 ${bulkPlan?.data.length ?? 0} 个代币会在同一笔交易中原子执行，成功后不可撤销。`
              : allMode
                ? '这是原子批量操作：全部成功才会生效，任意一个失败都会整笔回滚。'
                : copy.warning}
          </p>

          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-apple" disabled={mutation.isPending}>
              返回检查
            </AlertDialogCancel>
            <Button
              className="pressable rounded-apple"
              disabled={mutation.isPending}
              onClick={() =>
                allMode && bulkPlan
                  ? mutation.mutate({
                      mode: 'batch',
                      tokenAddresses: bulkPlan.data.map((token) => token.tokenAddress),
                    })
                  : mutation.mutate({ mode: 'single' })
              }
            >
              {mutation.isPending
                ? '等待钱包确认…'
                : allMode && bulkPlan
                  ? '在钱包中确认 1 笔批量交易'
                  : '在钱包中确认'}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  )
}

function TransactionNotice({ tone, hash }: { tone: 'success' | 'audit'; hash: string }) {
  const success = tone === 'success'
  const message = tone === 'success' ? '最近成功' : '交易已提交，等待链上核验或补写审计'
  return (
    <a
      href={explorerTxUrl(hash)}
      target="_blank"
      rel="noreferrer"
      className={`mt-3 flex items-center gap-2 rounded-apple px-3 py-2.5 text-[11.5px] ${
        success
          ? 'bg-green/[0.07] text-green hover:bg-green/[0.1]'
          : 'bg-warn/[0.08] text-warn hover:bg-warn/[0.12]'
      }`}
    >
      {success ? (
        <CheckCircle2 className="size-3.5 shrink-0" />
      ) : (
        <TriangleAlert className="size-3.5 shrink-0" />
      )}
      <span className="min-w-0 flex-1 truncate">
        {message}：{shortAddress(hash)}
      </span>
      <ExternalLink className="size-3 shrink-0" />
    </a>
  )
}

/**
 * 单币模式的链上现状。
 *
 * 「明确当前状态」不是装饰：撤销免税和添加免税走的是同一个 setTaxExempt，
 * 看不到现状就只能靠记忆判断这一步到底会不会改变什么。读失败时**不阻断**——
 * 链上模拟仍会在发交易前兜住，这里只降级成一句提示。
 */
function CurrentStatePanel({
  query,
  labels,
  desired,
  revocable,
  ready,
}: {
  query: ReturnType<typeof useQuery<boolean>>
  labels: { on: string; off: string }
  desired: boolean
  revocable: boolean
  ready: boolean
}) {
  if (!ready) {
    return (
      <div className="surface-inset flex items-center gap-2.5 px-3.5 py-2.5 text-[11.5px] leading-relaxed text-mut2">
        <span aria-hidden="true" className="size-1.5 shrink-0 rounded-full bg-edge/30" />
        填入代币与目标地址后，这里会显示该地址在链上的当前状态。
      </div>
    )
  }

  if (query.isLoading) {
    return (
      <div className="surface-inset flex items-center gap-2.5 px-3.5 py-2.5 text-[11.5px] text-mut">
        <Spinner className="size-3.5 shrink-0 text-cyan" />
        正在读取链上当前状态…
      </div>
    )
  }

  if (query.isError || typeof query.data !== 'boolean') {
    return (
      <div className="surface-inset flex items-start gap-2 px-3.5 py-2.5 text-[11.5px] leading-relaxed text-warn">
        <TriangleAlert className="mt-0.5 size-3.5 shrink-0" />
        <span className="min-w-0 flex-1">
          读不到链上当前状态，无法在此确认现状；提交前的链上模拟仍会拦截无效操作。
        </span>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="pressable -my-1 h-7 shrink-0 rounded-apple-sm text-[11px]"
          onClick={() => query.refetch()}
        >
          重试
        </Button>
      </div>
    )
  }

  const active = query.data
  const noop = active === desired

  return (
    <div
      role="status"
      aria-live="polite"
      className="surface-inset flex items-center gap-2.5 px-3.5 py-2.5 text-[11.5px]"
    >
      <span
        aria-hidden="true"
        className={cn('size-1.5 shrink-0 rounded-full', active ? 'bg-green' : 'bg-mut2')}
      />
      <span className="font-medium text-txt">{active ? labels.on : labels.off}</span>
      {noop ? (
        <span className="min-w-0 flex-1 truncate text-warn">
          {active && !revocable
            ? '· 已是目标状态，且合约不支持撤销'
            : '· 已是目标状态，这一步不会改变任何东西'}
        </span>
      ) : (
        <span className="min-w-0 flex-1 truncate text-mut2">
          · 本次操作会把它改成「{desired ? labels.on : labels.off}」
        </span>
      )}
    </div>
  )
}

function BulkPlanPanel({
  kind,
  enabled,
  plan,
  planning,
  canVerify,
  onVerify,
}: {
  kind: ContractWriteKind
  enabled: boolean
  plan: ContractOperationPlan | null
  planning: boolean
  canVerify: boolean
  onVerify: () => void
}) {
  if (planning) {
    return (
      <div className="surface-inset flex min-h-20 items-center gap-3 px-3.5 py-3 text-[11.5px] text-mut">
        <Spinner className="size-4 shrink-0 text-cyan" />
        正在读取当前 Manager 的代币、税率和免税状态，并逐个模拟调用…
      </div>
    )
  }

  if (!plan) {
    return (
      <div className="surface-inset flex items-center justify-between gap-3 px-3.5 py-3">
        <div>
          <p className="text-[12px] font-medium text-txt">先核验实际操作范围</p>
          <p className="mt-0.5 text-[11px] leading-relaxed text-mut2">
            {kind === 'detectedPool'
              ? '仅保留当前 Manager 管理、尚未登记且链上模拟添加成功的代币。'
              : enabled
                ? '仅保留当前 Manager 管理、存在税率、尚未免税且链上模拟成功的代币。'
                : '仅保留当前 Manager 管理、当前仍免税且链上模拟撤销成功的代币。'}
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="pressable shrink-0 rounded-apple-sm"
          disabled={!canVerify}
          onClick={onVerify}
        >
          核验范围
        </Button>
      </div>
    )
  }

  const metrics = [
    ['可操作', plan.data.length],
    ['当前管理', plan.managed],
    [kind === 'detectedPool' ? '候选代币' : '收税代币', plan.applicable],
    // alreadyDesired 是「已经处在目标状态」，方向反过来时它的中文说法也要反过来
    [kind === 'detectedPool' ? '已登记' : enabled ? '已免税' : '已非免税', plan.alreadyDesired],
  ] as const

  return (
    <div className="surface-inset space-y-3 px-3.5 py-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[11px] text-mut2">核验完成</p>
          <p className="mt-0.5 text-[20px] font-semibold tracking-tight text-txt">
            {plan.data.length}
            <span className="ml-1.5 text-[11.5px] font-normal text-mut">个代币可操作</span>
          </p>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="pressable h-8 rounded-apple-sm text-[11.5px]"
          disabled={!canVerify}
          onClick={onVerify}
        >
          <RefreshCw className="size-3.5" />
          重新核验
        </Button>
      </div>

      <dl className="grid grid-cols-2 gap-x-3 gap-y-2.5 border-t border-edge/60 pt-3 sm:grid-cols-4">
        {metrics.map(([label, value]) => (
          <div key={label} className="min-w-0">
            <dt className="truncate text-[11px] text-mut2 sm:text-[10.5px]">{label}</dt>
            <dd className="mt-0.5 text-[12px] font-medium tabular-nums text-txt">{value}</dd>
          </div>
        ))}
      </dl>

      {kind === 'detectedPool' && plan.data.length > 0 && (
        <ul className="flex max-h-[4.5rem] flex-wrap gap-1.5 overflow-y-auto border-t border-edge/60 pt-2.5">
          {plan.data.slice(0, 6).map((token) => (
            <li
              key={token.tokenAddress}
              className="rounded-full bg-edge/[0.06] px-2 py-0.5 text-[10.5px] text-mut"
            >
              {token.symbol ? (
                <span className="font-medium">{token.symbol}</span>
              ) : (
                <span className="font-mono">{shortAddress(token.tokenAddress)}</span>
              )}
            </li>
          ))}
          {plan.data.length > 6 && (
            <li className="px-1 py-0.5 text-[10.5px] tabular-nums text-mut2">
              +{plan.data.length - 6}
            </li>
          )}
        </ul>
      )}

      {(plan.ineligible > 0 || plan.data.length === 0) && (
        <p className="text-[10.5px] leading-relaxed text-mut2">
          {plan.ineligible > 0 && `${plan.ineligible} 个代币未通过链上模拟。`}
          {plan.data.length === 0 && ' 没有交易需要提交。'}
        </p>
      )}
    </div>
  )
}

function AddressField({
  id,
  label,
  placeholder,
  value,
  disabled,
  onChange,
}: {
  id: string
  label: string
  placeholder: string
  value: string
  disabled: boolean
  onChange: (value: string) => void
}) {
  const invalid = value.length > 0 && !isEvmAddress(value)

  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="text-[12.5px]">
        {label}
      </Label>
      <Input
        id={id}
        value={value}
        placeholder={placeholder}
        spellCheck={false}
        autoComplete="off"
        disabled={disabled}
        aria-invalid={invalid}
        className="h-9 rounded-apple font-mono text-[12px]"
        onChange={(event) => onChange(event.target.value)}
      />
      {invalid && <p className="text-[11px] text-rose">地址必须是 0x 开头的 40 位十六进制字符。</p>}
    </div>
  )
}

function ConfirmRow({
  label,
  value,
  mono = true,
}: {
  label: string
  value: string
  mono?: boolean
}) {
  return (
    <div className="space-y-1">
      <dt className="text-mut2">{label}</dt>
      <dd className={`break-all text-txt ${mono ? 'font-mono' : ''}`}>{value}</dd>
    </div>
  )
}
